import { DateWindow } from './DateWindow';
import { Messages } from '../../../localization';
import { DateWindowTypes } from '../core';
import { DateTrunc } from '../../../models';
import {
  abbreviatedRelativeDateWindow,
  dateFormatterFactory,
  windowPickerStartDate,
} from '../../../formatting/dateFormatter';

export const displayDateWindow = (dateWindow: DateWindow): string => {
  return displayStringFromWindow(dateWindow);
};

export const displayStringFromWindow = (window: DateWindow): string => {
  const granularity = granularityFromWindow(window);
  if (!granularity) {
    if (window.type === DateWindowTypes.CUSTOM || window.type === DateWindowTypes.DEFINED_START) {
      return Messages.metricsCustomStartDateOption();
    }
  } else if (window.type === DateWindowTypes.LAST_N) {
    const n = window.n;
    return rangeStringForGranularity(granularity, n);
  } else if (window.type === DateWindowTypes.LATEST_DATA) {
    const n = window.duration.numberOfUnits;
    return rangeStringForGranularity(granularity, n);
  }
  return Messages.metricsDateRangeAllTime();
};

const rangeStringForGranularity = (granularity: DateTrunc, n: number): string => {
  switch (granularity) {
    case DateTrunc.SECOND: {
      return n === 1
        ? Messages.metricsDateRangeSecondSingular()
        : Messages.metricsDateRangeSecondPlural({ num: n.toString() });
    }
    case DateTrunc.MINUTE: {
      return n === 1
        ? Messages.metricsDateRangeMinuteSingular()
        : Messages.metricsDateRangeMinutePlural({ num: n.toString() });
    }
    case DateTrunc.HOUR: {
      return n === 1
        ? Messages.metricsDateRangeHourSingular()
        : Messages.metricsDateRangeHourPlural({ num: n.toString() });
    }
    case DateTrunc.ISO_WEEKDAY:
    case DateTrunc.DAY: {
      return n === 1
        ? Messages.metricsDateRangeDaySingular()
        : Messages.metricsDateRangeDayPlural({ num: n.toString() });
    }
    case DateTrunc.MONTH: {
      return n === 1
        ? Messages.metricsDateRangeMonthSingular()
        : Messages.metricsDateRangeMonthPlural({ num: n.toString() });
    }
    case DateTrunc.ISO_WEEK:
    case DateTrunc.WEEK: {
      return n === 1
        ? Messages.metricsDateRangeWeekSingular()
        : Messages.metricsDateRangeWeekPlural({ num: n.toString() });
    }
    case DateTrunc.ISO_QTR:
    case DateTrunc.QUARTER: {
      return n === 1
        ? Messages.metricsDateRangeQuarterSingular()
        : Messages.metricsDateRangeQuarterPlural({ num: n.toString() });
    }
    case DateTrunc.ISO_YEAR:
    case DateTrunc.YEAR: {
      return n === 1
        ? Messages.metricsDateRangeYearSingular()
        : Messages.metricsDateRangeYearPlural({ num: n.toString() });
    }
    case DateTrunc.NONE: {
      return Messages.metricsDateRangeAllTime();
    }
    case DateTrunc.UNKNOWN:
    default: {
      return n === 1
        ? Messages.metricsDateRangeByMarkSingular()
        : Messages.metricsDateRangeByMarkPlural({ num: n.toString() });
    }
  }
};

export const granularityFromWindow = (window: DateWindow): DateTrunc | undefined => {
  // make it so that with new types of windows we don't have to add much more logic
  switch (window.type) {
    case DateWindowTypes.LAST_N:
      return DateTrunc.UNKNOWN;
    case DateWindowTypes.LATEST_DATA:
      return window.duration.unit;
    // our only "special" window at the moment is ALLTIME
    // this is likely to change in the future
    case DateWindowTypes.SPECIAL:
    case DateWindowTypes.CUSTOM:
    case DateWindowTypes.DEFINED_START:
    default:
      return undefined;
  }
};

function exhaustivelyMatch(_: never): never {
  throw 'Should not reach here';
}

export const getLabelForWindowPickerOption = (window: DateWindow): string => {
  switch (window.type) {
    case DateWindowTypes.DEFINED_START:
      return windowPickerStartDate(window.startDate);
    case DateWindowTypes.SPECIAL:
      return Messages.metricsDateRangeAllTime();
    case DateWindowTypes.LAST_N:
      return abbreviatedRelativeDateWindow(DateTrunc.UNKNOWN, window.n);
    case DateWindowTypes.LATEST_DATA:
      return abbreviatedRelativeDateWindow(window.duration.unit, window.duration.numberOfUnits);
    case DateWindowTypes.CUSTOM:
      return dateFormatterFactory(DateTrunc.UNKNOWN).dateRangeSlashFormat(window.range.first, window.range.last);
    default:
      exhaustivelyMatch(window);
  }
};
